// Own-library → "Your substances": search + select an existing substance
// from the user's Substance Library as the target reference.
// Ported from the Claude Design prototype (anchor-target.jsx, LibrarySelect).
//
// Wired against GET /me/inventory/library-items (useInventoryLibraryItems).
// Each library item groups inventory lots of one substance; we resolve its
// target ref from the first child carrying a (substance_ndb_table, rowid)
// pair → public.food (mixture) | public.compound (compound). Items with no
// resolvable substance child are skipped — they can't anchor a target.

import { useMemo } from "react";
import { Icon } from "@/components/Icon/Icon";
import { useInventoryLibraryItems } from "@/hooks/useInventoryLibraryItems";
import type { LibraryItem } from "@/services/inventory_types";

export type PickedLibrarySubstance = {
  id: string;
  ndbTable: "public.food" | "public.compound";
  rowId: number;
  name: string;
};

type LibrarySelectProps = {
  libSearch: string;
  onLibSearchChange: (v: string) => void;
  pickedLibId: string | null;
  onPick: (picked: PickedLibrarySubstance | null) => void;
  onOpenLibrary: () => void;
};

type LibraryRow = {
  id: string;
  name: string;
  code: string;
  family: string | null;
  isMix: boolean;
  ndbTable: "public.food" | "public.compound";
  rowId: number;
};

// Resolve a library item's target ref from its first inventory child that
// points at a public.food / public.compound row. Returns null when none does.
function toLibraryRow(item: LibraryItem): LibraryRow | null {
  const child = item.inventory.find(
    (c) =>
      (c.substanceNdbTable === "public.food" || c.substanceNdbTable === "public.compound") &&
      typeof c.substanceRowid === "number",
  );
  if (!child || child.substanceRowid == null) return null;
  const ndbTable = child.substanceNdbTable as "public.food" | "public.compound";
  return {
    id: item.id,
    name: item.name,
    code: item.code,
    family: child.families?.[0]?.displayText ?? null,
    isMix: ndbTable === "public.food",
    ndbTable,
    rowId: child.substanceRowid,
  };
}

export function LibrarySelect({
  libSearch,
  onLibSearchChange,
  pickedLibId,
  onPick,
  onOpenLibrary,
}: LibrarySelectProps) {
  const q = (libSearch || "").trim();
  const { data, isLoading, isError } = useInventoryLibraryItems({
    filters: q.length > 0 ? { search: q } : undefined,
  });

  const results = useMemo<LibraryRow[]>(() => {
    const items = data?.pages.flatMap((p) => p.items) ?? [];
    return items
      .map(toLibraryRow)
      .filter((r): r is LibraryRow => r !== null)
      .slice(0, 60);
  }, [data]);

  return (
    <div className="vstack gap-2">
      <div
        className="hstack"
        style={{ justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}
      >
        <div className="label">Search your Substance Library</div>
        <button type="button" className="btn xs ghost" onClick={onOpenLibrary} style={{ fontSize: 11 }}>
          Open Substance Library
          <Icon name="external" size={10} />
        </button>
      </div>

      <div
        className="hstack"
        style={{
          gap: 8,
          padding: "8px 10px",
          border: "1px solid var(--line)",
          borderRadius: 6,
          background: "var(--bg)",
        }}
      >
        <Icon name="search" size={12} />
        <input
          value={libSearch}
          onChange={(e) => onLibSearchChange(e.target.value)}
          placeholder="Search by name, library code, or identifier…"
          style={{ flex: 1, background: "transparent", border: 0, fontSize: 13, outline: "none" }}
        />
        <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
          {isLoading ? "loading…" : `${results.length} shown`}
        </span>
      </div>

      {isError ? (
        <div style={{ padding: "28px 16px", textAlign: "center", color: "var(--err, #c33)", fontSize: 13 }}>
          Couldn't load your Substance Library. Try again.
        </div>
      ) : isLoading ? (
        <div style={{ padding: "28px 16px", textAlign: "center", color: "var(--ink-4)", fontSize: 13 }}>
          Loading your substances…
        </div>
      ) : results.length === 0 ? (
        <div style={{ padding: "28px 16px", textAlign: "center", color: "var(--ink-4)", fontSize: 13 }}>
          {q
            ? <>Nothing in your library matched <span className="mono">"{libSearch}"</span>.</>
            : "No substances with a resolved profile yet. Add one via “Add new substance”."}
        </div>
      ) : (
        <div
          style={{
            maxHeight: 320,
            overflowY: "auto",
            border: "1px solid var(--line)",
            borderRadius: 8,
            background: "var(--bg)",
          }}
        >
          {results.map((it, i) => {
            const sel = pickedLibId === it.id;
            const isMix = it.isMix;
            return (
              <button
                key={it.id}
                type="button"
                onClick={() =>
                  onPick(
                    sel
                      ? null
                      : { id: it.id, ndbTable: it.ndbTable, rowId: it.rowId, name: it.name },
                  )
                }
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  textAlign: "left",
                  background: sel ? "var(--accent-soft)" : "transparent",
                  borderBottom: i === results.length - 1 ? "none" : "1px solid var(--line-2)",
                  borderLeft: `3px solid ${sel ? "var(--accent-ink)" : "transparent"}`,
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!sel) e.currentTarget.style.background = "var(--bg-sunk)";
                }}
                onMouseLeave={(e) => {
                  if (!sel) e.currentTarget.style.background = "transparent";
                }}
              >
                <span
                  title={isMix ? "Mixture / extract" : "Compound"}
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 5,
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isMix ? "var(--signal-soft)" : "var(--accent-soft)",
                    color: isMix ? "#7c5813" : "var(--accent-ink)",
                    border: `1px solid ${isMix ? "#e7c97a" : "var(--accent-line)"}`,
                  }}
                >
                  <Icon name={isMix ? "molecule" : "atom"} size={12} />
                </span>
                <div className="vstack" style={{ flex: 1, minWidth: 0, gap: 2, lineHeight: 1.25 }}>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {it.name}
                  </span>
                  <div className="hstack" style={{ gap: 8, minWidth: 0, alignItems: "center" }}>
                    <span className="mono" style={{ fontSize: 10, color: "var(--accent-ink)", fontWeight: 600 }}>
                      {it.code}
                    </span>
                    {it.family && (
                      <span className="mono" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
                        {it.family}
                      </span>
                    )}
                    <span
                      className="mono"
                      style={{
                        fontSize: 8.5,
                        fontWeight: 600,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: isMix ? "#7c5813" : "var(--accent-ink)",
                      }}
                    >
                      {isMix ? "Mixture" : "Compound"}
                    </span>
                  </div>
                </div>
                {sel && (
                  <span style={{ color: "var(--accent-ink)", flexShrink: 0 }}>
                    <Icon name="check" size={15} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
